import { Box, Typography, makeStyles } from "@material-ui/core";
import { useSnackbar } from "notistack";
import React from "react";
import { useHistory } from "react-router";

import { CustomButton } from "@/components/Button";
import { buildDiagnosisPhaseTags } from "@/components/diagnosis/DiagnosisPhaseTags";
import { LoadingOverlay } from "@/components/LoadingOverlay";
import { fontFamilies } from "@/designTokens";
import { useFromHomeEntry } from "@/hooks/useFromHomeEntry";
import { DiagnosisLayout, diagnosisActionStyles } from "@/layouts/DiagnosisLayout";
import theme from "@/theme";

import AnxietyStep from "./components/steps/Anxiety";
import AvoidStep from "./components/steps/Avoid";
import ExplanationStep from "./components/steps/Explanation";
import HoldingStep from "./components/steps/Holding";
import InterestStep from "./components/steps/Interest";
import LocationYieldStep from "./components/steps/LocationYield";
import ManagementStep from "./components/steps/Management";
import MeetingItemsStep from "./components/steps/MeetingItems";
import MonthlyStep from "./components/steps/Monthly";
import PurposeStep from "./components/steps/Purpose";
import SaleTimingStep from "./components/steps/SaleTiming";
import SuccessStep from "./components/steps/Success";
import { TOTAL_STEPS, useInvestmentStyleDiagnosisForm } from "./hooks/useInvestmentStyleDiagnosisForm";
import { useSubmitInvestmentStyleDiagnosis } from "./hooks/useSubmitInvestmentStyleDiagnosis";
import { buildSubmitStep } from "./utils/submitStep";

const useStyles = makeStyles(() => ({
  progress: { marginTop: -8, marginBottom: 24 },
  progressTrack: {
    width: "100%",
    height: 3,
    borderRadius: 9999,
    backgroundColor: theme.palette.semantic["surface/base"],
    overflow: "hidden",
  },
  progressBar: {
    height: "100%",
    borderRadius: 9999,
    backgroundColor: theme.palette.primary.main,
    transition: "width 0.3s ease",
  },
  counter: {
    marginTop: 16,
    fontSize: 12,
    lineHeight: 1.5,
    fontFamily: fontFamilies.number,
    color: theme.palette.text.secondary,
    textAlign: "center",
  },
  buttonWrap: diagnosisActionStyles,
  back: { marginTop: 4 },
}));

export const DiagnosisInvestmentStylePage: React.FC = () => {
  const classes = useStyles();
  const history = useHistory();
  const form = useInvestmentStyleDiagnosisForm();
  const { submit, submitting } = useSubmitInvestmentStyleDiagnosis();
  const fromHome = useFromHomeEntry();
  const { enqueueSnackbar } = useSnackbar();

  // 設問の切り替わりで先頭に戻す。次へ／戻るのどちらでも同じ位置から読み始められるようにする
  React.useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [form.step]);

  // 完了直後のブラウザバック等でこのルートへ戻ってきた場合、フックが結果画面への history.replace を
  // 実行中。フォームが一瞬でも見えないよう、ここでは何も描画せずローディング表示のみにする。
  if (form.isRedirectingToResult) {
    return <LoadingOverlay open />;
  }

  const handleNext = () => {
    if (form.isLastStep) {
      // 修正B（最終防衛線）: フック側の継続的ガード（修正A）を潜り抜けて到達した場合に備え、
      // 送信直前に 0〜11 の全 step を再検証する。無効な step があれば送信せずそこへ差し戻す。
      const invalidStep = form.firstInvalidStep(TOTAL_STEPS);
      if (invalidStep !== null) {
        enqueueSnackbar("未入力の項目があります。内容をご確認ください。", { variant: "error" });
        form.goToStep(invalidStep);
        return;
      }
      submit(form.buildRequest());
      return;
    }
    form.goNext();
  };

  const renderStep = () => {
    switch (form.step) {
      case 0:
        return (
          <PurposeStep
            purposes={form.answers.purposes}
            onPurposesChange={form.handlePurposesChange}
            primaryOptions={form.primaryOptions}
            primaryPurpose={form.answers.primary_purpose}
            onPrimaryPurposeChange={(next) => form.update("primary_purpose", next)}
            idealText={form.answers.ideal_text}
            onIdealTextChange={(next) => form.update("ideal_text", next)}
          />
        );
      case 1:
        return (
          <InterestStep interest={form.answers.interest} onInterestChange={(next) => form.update("interest", next)} />
        );
      case 2:
        return (
          <SuccessStep
            successConditions={form.answers.success_conditions}
            onSuccessConditionsChange={(next) => form.update("success_conditions", next)}
          />
        );
      case 3:
        return (
          <MonthlyStep
            monthlyOutOfPocket={form.answers.monthly_out_of_pocket}
            onMonthlyOutOfPocketChange={(next) => form.update("monthly_out_of_pocket", next)}
          />
        );
      case 4:
        return (
          <HoldingStep
            holdingPeriod={form.answers.holding_period}
            onHoldingPeriodChange={(next) => form.update("holding_period", next)}
          />
        );
      case 5:
        return (
          <LocationYieldStep
            locationYieldPreference={form.answers.location_yield_preference}
            onLocationYieldPreferenceChange={(v) => form.update("location_yield_preference", v)}
          />
        );
      case 6:
        return (
          <ManagementStep
            managementStyle={form.answers.management_style}
            onManagementStyleChange={(next) => form.update("management_style", next)}
          />
        );
      case 7:
        return (
          <SaleTimingStep
            shortTermSaleIntent={form.answers.short_term_sale_intent}
            onShortTermSaleIntentChange={(next) => form.update("short_term_sale_intent", next)}
          />
        );
      case 8:
        return (
          <MeetingItemsStep
            meetingCheckItems={form.answers.meeting_check_items}
            onMeetingCheckItemsChange={(next) => form.update("meeting_check_items", next)}
          />
        );
      case 9:
        return (
          <ExplanationStep
            explanationStyles={form.answers.explanation_styles}
            onExplanationStylesChange={(next) => form.update("explanation_styles", next)}
            meetingFreeText={form.answers.meeting_free_text}
            onMeetingFreeTextChange={(v) => form.update("meeting_free_text", v)}
          />
        );
      case 10:
        return (
          <AnxietyStep
            anxiety={form.answers.anxiety}
            onAnxietyChange={(key, level) => form.update("anxiety", { ...form.answers.anxiety, [key]: level })}
          />
        );
      case 11:
        return (
          <AvoidStep
            avoidSalesStyles={form.answers.avoid_sales_styles}
            onAvoidSalesStylesChange={(next) => form.update("avoid_sales_styles", next)}
          />
        );
      default:
        return null;
    }
  };

  return (
    <DiagnosisLayout phaseTags={buildDiagnosisPhaseTags(fromHome, false)}>
      <Box className={classes.progress}>
        <Box
          className={classes.progressTrack}
          role="progressbar"
          aria-label="診断の進捗"
          aria-valuemin={0}
          aria-valuemax={TOTAL_STEPS}
          aria-valuenow={form.step + 1}
        >
          <Box className={classes.progressBar} style={{ width: `${((form.step + 1) / TOTAL_STEPS) * 100}%` }} />
        </Box>
        <Typography className={classes.counter}>
          {form.step + 1} / {TOTAL_STEPS}
        </Typography>
      </Box>
      {renderStep()}
      <Box className={classes.buttonWrap}>
        <CustomButton
          type="button"
          tone="primary"
          buttonSize="L"
          fullWidth
          disabled={!form.isStepValid(form.step) || submitting}
          onClick={handleNext}
        >
          {/* 最後の設問だけ、送信後の遷移先に合わせた文言にする */}
          {form.isLastStep ? buildSubmitStep(fromHome).label : "次へ"}
        </CustomButton>
      </Box>
      <Box className={`${classes.buttonWrap} ${classes.back}`}>
        <CustomButton
          type="button"
          tone="text"
          buttonSize="L"
          fullWidth
          disabled={submitting}
          onClick={() => {
            if (fromHome) history.goBack();
            else if (form.step > 0) form.goToStep(form.step - 1);
            else history.push("/investment_style_diagnosis/intro", { fromHome });
          }}
        >
          戻る
        </CustomButton>
      </Box>
      <LoadingOverlay open={submitting} />
    </DiagnosisLayout>
  );
};
